// 194 -- one directions-URL builder for every "Get directions" affordance
// (event header, schedule venue bar/pills, match detail). Replaces the
// divergent ad-hoc builders that each reached for a different level of
// precision.
//
// Uses the Maps *search* endpoint, NOT the directions (`/maps/dir/`) endpoint.
// Directions mode needs an ORIGIN, and we never have the user's location, so
// Maps falls back to geolocation -- which pends/denies on desktop and leaves a
// blank spinner, especially for a venue that has only a name to route to.
// Search mode renders the destination immediately (pinned exactly by place id
// when we have one) and Google's own "Directions" button then handles the
// origin in the Maps UI. This is what the pre-194 match-detail link did.
//
// Precision order: Google place id > GPS coordinates > text query.
// The result is always an `https://www.google.com/maps/...` URL, which deep
// links into the native Maps app on mobile and passes the 178 safe-url
// allowlist (the old `geo:` pills did neither of those cleanly).

export interface DirectionsTarget {
  display_name: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_place_id?: string | null;
}

export function directionsUrl(target: DirectionsTarget): string {
  const { latitude, longitude, google_place_id, address, display_name } = target;
  const hasGps = latitude != null && longitude != null;
  // With a place id, the query is just a readable label -- query_place_id pins
  // the exact listing (and is a meaningful fallback if the id ever goes stale).
  // Without one, prefer GPS coords, then the address/name text.
  const query = !google_place_id && hasGps ? `${latitude},${longitude}` : (address || display_name);
  const params = new URLSearchParams({ api: "1", query });
  if (google_place_id) params.set("query_place_id", google_place_id);
  return `https://www.google.com/maps/search/?${params.toString()}`;
}
